/**
 * ExampleScreensFilmstrip — horizontal strip of REAL screen previews for a
 * library example (V2ScreenView previewMode="static" inside
 * AutoScaleScreenContainer — same mechanism as the onboarding template panel).
 * Closes the AC-P3-009 schematic-thumbnails TODO wherever it is mounted.
 *
 * The screens come from the example's render-only `previewScreens` — full
 * StepNodes captured from the source strategy.
 */

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { V2ScreenView, AutoScaleScreenContainer } from '@modules/screen-view-v2';
import { CANVAS_NODE_DIMENSIONS } from '@modules/reactFlow/constants/screenPreviewConfig';
import type { StepNode } from '@segmently/schema-models/v2';

/**
 * Thumbnails keep the CANVAS proportions: standard screens = the 430×932
 * canvas node ratio; free-form screens with their own `containerSize` render
 * TRUE-PROPORTION (same rule as FlowOnboardingScreenNodeV2). Height is fixed
 * per size; width follows the ratio (clamped for extreme landscape shapes).
 */
const SIZES = {
  /** BuildingBlocksSection preview pane. */
  small: { height: 150 },
  /** Details dialog / browser detail view. */
  medium: { height: 236 },
} as const;

function contentDims(screen: Record<string, unknown>): { width: number; height: number } {
  const container = screen.containerSize as { width?: number; height?: number } | undefined;
  if (typeof container?.width === 'number' && typeof container?.height === 'number'
    && container.width > 0 && container.height > 0) {
    return { width: container.width, height: container.height };
  }
  return { width: CANVAS_NODE_DIMENSIONS.WIDTH, height: CANVAS_NODE_DIMENSIONS.HEIGHT };
}

export interface ExampleScreensFilmstripProps {
  screens: Array<Record<string, unknown>>;
  size?: keyof typeof SIZES;
  /** When set, thumbnails are buttons (enlarge affordance). */
  onScreenClick?: (index: number) => void;
  'data-testid'?: string;
}

export const ExampleScreensFilmstrip: React.FC<ExampleScreensFilmstripProps> = ({
  screens,
  size = 'medium',
  onScreenClick,
  'data-testid': testId = 'example-screens-filmstrip',
}) => {
  const dims = SIZES[size];
  if (screens.length === 0) return null;
  return (
    <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 0.5 }} data-testid={testId}>
      {screens.map((screen, index) => {
        const caption = String(screen.name ?? screen.screenType ?? `Screen ${index + 1}`);
        const kind = typeof screen.screenType === 'string' ? screen.screenType : undefined;
        const content = contentDims(screen);
        // Canvas-true aspect ratio; clamp width for extreme landscape shapes
        // so one wide free-form screen cannot eat the whole strip.
        const thumbWidth = Math.min(
          Math.round((dims.height * content.width) / content.height),
          Math.round(dims.height * 0.75),
        );
        return (
          <Box key={String(screen.id ?? index)} sx={{ flex: '0 0 auto', width: thumbWidth }}>
            <Box
              role={onScreenClick ? 'button' : undefined}
              tabIndex={onScreenClick ? 0 : undefined}
              aria-label={onScreenClick ? `Enlarge screen ${caption}` : undefined}
              data-testid={`${testId}-screen-${index}`}
              onClick={onScreenClick ? () => onScreenClick(index) : undefined}
              onKeyDown={onScreenClick
                ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onScreenClick(index);
                  }
                }
                : undefined}
              sx={{
                width: thumbWidth,
                height: dims.height,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.default',
                cursor: onScreenClick ? 'pointer' : 'default',
                '&:hover': onScreenClick ? { borderColor: 'primary.main' } : undefined,
                '&:focus-visible': onScreenClick
                  ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 }
                  : undefined,
              }}
            >
              <AutoScaleScreenContainer
                scaleOptions={{ contentWidth: content.width, contentHeight: content.height, padding: 0 }}
                showBeforeReady
              >
                <div style={{ width: content.width, height: content.height, pointerEvents: 'none' }}>
                  <V2ScreenView
                    step={screen as unknown as StepNode}
                    locale="en-US"
                    previewMode="static"
                  />
                </div>
              </AutoScaleScreenContainer>
            </Box>
            <Typography variant="caption" component="div" noWrap sx={{ textAlign: 'center', mt: 0.5 }}>
              {caption}
            </Typography>
            {kind && (
              <Typography
                variant="caption"
                component="div"
                noWrap
                color="text.secondary"
                sx={{ textAlign: 'center', fontSize: 10 }}
              >
                {kind}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

ExampleScreensFilmstrip.displayName = 'ExampleScreensFilmstrip';
